import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardPasswordGate from '../analytics-dashboard/components/DashboardPasswordGate';
import FunnelChart from '../analytics-dashboard/components/FunnelChart';
import UtmDropOffTable from '../analytics-dashboard/components/UtmDropOffTable';
import BeraterMetrics from '../analytics-dashboard/components/BeraterMetrics';
import { fetchBookings, fetchFunnelData, isAuthenticated } from '../../services/apiService';


const AnalyticsScreen = () => {
  const [authenticated, setAuthenticated] = useState(isAuthenticated());
  const [period, setPeriod] = useState('30');
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [funnelData, setFunnelData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!authenticated) return;
    setLoading(true);
    Promise.all([fetchBookings(), fetchFunnelData()])?.then(([bData, fData]) => {
      const normalized = bData?.map(b => ({
        id: b?.id,
        timestamp: b?.created_at,
        type: b?.type,
        berater: b?.berater_name,
        webinar_id: b?.webinar_id,
        utm_source: b?.utm_source,
        utm_medium: b?.utm_medium,
        utm_campaign: b?.utm_campaign,
      })) || [];
      setBookings(normalized);
      setFunnelData(fData);
      setLoading(false);
    });
  }, [authenticated]);

  const filteredBookings = useMemo(() => {
    if (period === 'all') return bookings;
    const days = parseInt(period);
    const cutoff = new Date();
    cutoff?.setDate(cutoff?.getDate() - days);
    return bookings?.filter(b => new Date(b?.timestamp) >= cutoff);
  }, [period, bookings]);

  const totalSessions = funnelData?.dsgvo || bookings?.length || 0;
  const completedBookings = funnelData?.booking_completed || bookings?.length || 0;

  const conversionRate = totalSessions > 0
    ? ((completedBookings / totalSessions) * 100)?.toFixed(1)
    : '0.0';

  const periods = [
    { value: '30', label: '30 Tage' },
    { value: '90', label: '90 Tage' },
    { value: 'all', label: 'Gesamt' },
  ];

  if (!authenticated) {
    return <DashboardPasswordGate onSuccess={() => setAuthenticated(true)} />;
  }

  const kpis = [
    { label: 'Sitzungen gesamt', value: totalSessions?.toLocaleString('de-DE'), sub: 'Formular gestartet', accent: false },
    { label: 'DSGVO akzeptiert', value: (funnelData?.dsgvo || 0)?.toLocaleString('de-DE'), sub: totalSessions > 0 ? Math.round(((funnelData?.dsgvo || 0) / totalSessions) * 100) + '% der Sitzungen' : '–', accent: false },
    { label: 'Buchungen abgeschlossen', value: completedBookings?.toLocaleString('de-DE'), sub: 'Vollstaendige Buchungen', accent: true },
    { label: 'Gesamt-Conversion', value: conversionRate + '%', sub: 'Start bis Buchung', accent: true },
    { label: 'Groesster Abbruch', value: 'Schritt 5', sub: 'Kontaktdaten (-41%)', accent: false },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f6f8', fontFamily: 'var(--font-ui)' }}>
      <header style={{
        background: 'white', borderBottom: '1px solid #dde1e7',
        padding: '0 24px', height: '64px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 1px 3px rgba(62,73,84,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/analytics-dashboard')}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: '1px solid #dde1e7', background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#6b7a8d', fontFamily: 'var(--font-ui)', transition: 'all 150ms ease' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f5f6f8'; e.currentTarget.style.color = '#1e2b38'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#6b7a8d'; }}
          >
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'><polyline points='15 18 9 12 15 6'/></svg>
            Dashboard
          </button>
          <div style={{ width: '1px', height: '20px', background: '#dde1e7' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#3E4954', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><polygon points='22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3'/></svg>
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: '#1e2b38', lineHeight: 1.1 }}>Funnel-Analyse</div>
              <div style={{ fontSize: '11px', color: '#6b7a8d' }}>ZBA - Conversion Tracking</div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f5f6f8', borderRadius: '12px', padding: '4px', border: '1px solid #dde1e7' }}>
          {periods?.map(p => (
            <button key={p?.value} onClick={() => setPeriod(p?.value)} style={{
              padding: '6px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, fontFamily: 'var(--font-ui)', transition: 'all 200ms ease',
              background: period === p?.value ? '#3E4954' : 'transparent',
              color: period === p?.value ? '#ffffff' : '#6b7a8d',
              boxShadow: period === p?.value ? '0 1px 4px rgba(62,73,84,0.2)' : 'none',
            }}>{p?.label}</button>
          ))}
        </div>
      </header>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#8a95a3', fontSize: '14px' }}>
            <div style={{ fontSize: '24px', marginBottom: '12px' }}>⏳</div>
            Daten werden geladen...
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '32px' }}>
              {kpis?.map((kpi, i) => (
                <div key={i} style={{
                  background: kpi?.accent ? '#3E4954' : 'white',
                  borderRadius: '16px', padding: '20px',
                  border: kpi?.accent ? 'none' : '1px solid #dde1e7',
                  boxShadow: kpi?.accent ? '0 4px 20px rgba(62,73,84,0.2)' : '0 1px 3px rgba(62,73,84,0.06)',
                }}>
                  <div style={{ fontSize: '30px', fontWeight: 700, color: kpi?.accent ? 'white' : '#1e2b38', fontFamily: 'var(--font-heading)', lineHeight: 1, marginBottom: '6px', letterSpacing: '-0.02em' }}>{kpi?.value}</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: kpi?.accent ? 'rgba(255,255,255,0.9)' : '#1e2b38', marginBottom: '2px' }}>{kpi?.label}</div>
                  <div style={{ fontSize: '11px', color: kpi?.accent ? 'rgba(255,255,255,0.6)' : '#6b7a8d' }}>{kpi?.sub}</div>
                </div>
              ))}
            </div>

            <FunnelChart funnelData={funnelData || { dsgvo: 0, type_selected: 0, date_selected: 0, slot_selected: 0, contact_filled: 0, summary_viewed: 0, booking_completed: 0 }} totalSessions={totalSessions} />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(480px, 1fr))', gap: '24px', alignItems: 'start' }}>
              <UtmDropOffTable bookings={filteredBookings} funnelByUtm={null} />
              <BeraterMetrics bookings={filteredBookings} />
            </div>

            <div style={{ marginTop: '8px', padding: '14px 20px', background: 'rgba(62,73,84,0.04)', borderRadius: '12px', border: '1px solid #dde1e7', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='#6b7a8d' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' style={{ flexShrink: 0, marginTop: '1px' }}>
                <circle cx='12' cy='12' r='10'/><line x1='12' y1='8' x2='12' y2='12'/><line x1='12' y1='16' x2='12.01' y2='16'/>
              </svg>
              <p style={{ fontSize: '12px', color: '#6b7a8d', margin: 0, lineHeight: 1.6 }}>
                <strong style={{ color: '#3E4954' }}>Hinweis:</strong> Funnel-Daten basieren auf Eintraegen in der Supabase-Tabelle <code>booking_logs</code>. Buchungsabschluesse werden automatisch getrackt. Fuer vollstaendiges Schritt-Tracking kann <code>insertBookingLog</code> bei jedem Schrittwechsel aufgerufen werden.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnalyticsScreen;
